// Inline (quick) edit: one cell, one write.
//
// The source enables quick edit on every barrier grid except `Plants`, so this
// is not a convenience added here -- it is how the people using the application
// change a commitment date today. What it sends is a PATCH carrying the one
// field, which is also what makes a failure legible: a 422 names the field it
// refused and the cell shows it in place.

import { useEffect, useState } from 'react';
import type { FieldType } from '../schema';
import { editValue, renderValue } from './DataGrid';

export interface EditableCellProps {
  value: unknown;
  /** What to show when the cell is not being edited, where that is not the
   *  value being edited -- a foreign key shows its resolved name. */
  display?: string;
  type: FieldType;
  options?: { value: string | number; label: string }[];
  onCommit: (value: unknown) => Promise<void>;
}

export function EditableCell({ value, display, type, options, onCommit }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => editValue(value, type));
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  // The row can be refetched under the cell after a write elsewhere.
  useEffect(() => {
    if (!editing) setDraft(editValue(value, type));
  }, [value, type, editing]);

  // A boolean has no text to type, so it is a checkbox that writes on change
  // rather than a field with a commit step.
  if (type === 'bool') {
    return (
      <input
        type="checkbox"
        checked={Boolean(value)}
        disabled={saving}
        onClick={(event) => event.stopPropagation()}
        onChange={async (event) => {
          setSaving(true);
          try {
            await onCommit(event.target.checked);
            setFailed(false);
          } catch {
            setFailed(true);
          } finally {
            setSaving(false);
          }
        }}
      />
    );
  }

  async function commit(next: string): Promise<void> {
    const parsed = parse(next, type);
    if (parsed === editValueAsParsed(value, type)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onCommit(parsed);
      setFailed(false);
      setEditing(false);
    } catch {
      // Left open, holding what was typed, so the failure is beside the value
      // that caused it rather than replacing it.
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <span
        className={failed ? 'cell-failed' : undefined}
        tabIndex={0}
        role="button"
        onClick={(event) => {
          event.stopPropagation();
          setEditing(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.stopPropagation();
            setEditing(true);
          }
        }}
      >
        {display ?? (renderValue(value, type) || '\u00a0')}
      </span>
    );
  }

  const shared = {
    autoFocus: true,
    disabled: saving,
    onClick: (event: { stopPropagation: () => void }) => event.stopPropagation(),
    onBlur: () => void commit(draft),
  };

  if (options) {
    return (
      <select
        {...shared}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          void commit(event.target.value);
        }}
      >
        <option value="">(none)</option>
        {options.map((option) => (
          <option key={option.value} value={String(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      {...shared}
      type={type === 'date' ? 'date' : type === 'int' ? 'number' : 'text'}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') void commit(draft);
        if (event.key === 'Escape') {
          setDraft(editValue(value, type));
          setEditing(false);
        }
      }}
    />
  );
}

/** What goes in the PATCH body.
 *
 *  An emptied field is `null` rather than `""`: the API's validation refuses an
 *  empty string on a non-nullable column and stores one on a nullable one, and
 *  a person clearing a cell means the value is gone. */
export function parse(text: string, type: FieldType): unknown {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (type === 'int') {
    const number = Number(trimmed);
    return Number.isFinite(number) ? number : trimmed;
  }
  return trimmed;
}

function editValueAsParsed(value: unknown, type: FieldType): unknown {
  return parse(editValue(value, type), type);
}
